import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { PERMISSIONS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { CopyField } from '@/components/copy-field';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { messageForError } from '@/lib/api-client';
import {
  organisationDetailQuery,
  organisationKeys,
  organisationsApi,
} from '@/lib/api/organisations';
import { clearLastOrgId } from '@/lib/auth-store';
import { formatDate } from '@/lib/utils';

const settingsSchema = z.object({
  name: z.string().min(1, 'Give the organisation a name').max(200),
  billingEmail: z.union([z.email('Enter a valid email address'), z.literal('')]),
  defaultLocale: z
    .string()
    .min(2, 'Use a locale code such as en-GB')
    .max(20)
    .regex(/^[a-z]{2}(-[A-Za-z0-9]{2,8})?$/, 'Use a locale code such as en-GB'),
});

type SettingsValues = z.infer<typeof settingsSchema>;

function SettingsPage() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const organisation = useQuery(organisationDetailQuery(orgId));
  const [deleteOpen, setDeleteOpen] = useState(false);

  const form = useForm<SettingsValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: { name: '', billingEmail: '', defaultLocale: 'en-GB' },
  });

  const { reset } = form;
  useEffect(() => {
    if (!organisation.data) return;
    reset({
      name: organisation.data.name,
      billingEmail: organisation.data.billingEmail ?? '',
      defaultLocale: organisation.data.defaultLocale,
    });
  }, [organisation.data, reset]);

  const updateMutation = useMutation({
    mutationFn: (values: SettingsValues) =>
      organisationsApi.update(orgId, {
        name: values.name,
        billingEmail: values.billingEmail === '' ? null : values.billingEmail,
        defaultLocale: values.defaultLocale,
      }),
    onSuccess: () => {
      toast.success('Organisation updated');
      void queryClient.invalidateQueries({ queryKey: organisationKeys.all });
    },
    onError: (error) => toast.error(messageForError(error, 'We could not save those changes.')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => organisationsApi.remove(orgId),
    onSuccess: () => {
      clearLastOrgId();
      toast.success('Organisation deleted');
      void queryClient.invalidateQueries({ queryKey: organisationKeys.all });
      void navigate({ to: '/app' });
    },
    onError: (error) =>
      toast.error(messageForError(error, 'We could not delete this organisation.')),
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Organisation"
        title="Settings"
        description="Identity and defaults for this workspace. Changes apply to every site in the organisation."
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>General</CardTitle>
            <CardDescription>
              The name your team sees, where billing correspondence goes, and the locale new content
              defaults to.
            </CardDescription>
          </CardHeader>

          {organisation.isLoading ? (
            <CardContent className="flex flex-col gap-4">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </CardContent>
          ) : (
            <Form {...form}>
              <form
                noValidate
                onSubmit={form.handleSubmit((values) => updateMutation.mutate(values))}
              >
                <CardContent className="flex flex-col gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organisation name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="billingEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Billing email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="accounts@company.com" {...field} />
                        </FormControl>
                        <FormDescription>
                          Invoices and payment failures go here. Leave blank to use the owner&rsquo;s
                          address.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="defaultLocale"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default locale</FormLabel>
                        <FormControl>
                          <Input placeholder="en-GB" className="font-mono" {...field} />
                        </FormControl>
                        <FormDescription>
                          Applied to new entries. Existing content keeps its own locale.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <PermissionGate
                    permission={PERMISSIONS.ORG_SETTINGS_MANAGE}
                    fallback={
                      <p className="text-micro text-ink-subtle">
                        You have read-only access to these settings.
                      </p>
                    }
                  >
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        type="submit"
                        disabled={updateMutation.isPending || !form.formState.isDirty}
                      >
                        {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={!form.formState.isDirty}
                        onClick={() => reset()}
                      >
                        Discard
                      </Button>
                    </div>
                  </PermissionGate>
                </CardContent>
              </form>
            </Form>
          )}
        </Card>

        <Card className="h-fit">
          <CardHeader>
            <span className="section-label">Identifiers</span>
            <CardTitle>For your records</CardTitle>
            <CardDescription>
              Quote the organisation ID when contacting support or wiring up the SDK.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <CopyField label="Organisation ID" value={orgId} />
            {organisation.data ? (
              <>
                <CopyField label="Slug" value={organisation.data.slug} />
                <div>
                  <span className="section-label">Created</span>
                  <p className="text-data text-ink-muted" data-numeric>
                    {formatDate(organisation.data.createdAt)}
                  </p>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <PermissionGate permission={PERMISSIONS.ORG_DELETE}>
        <section className="rounded-lg border border-danger/35 bg-danger-soft/40">
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
            <div className="max-w-prose">
              <h2 className="section-label text-danger">Danger zone</h2>
              <p className="mt-1 text-data font-medium text-ink">Delete this organisation</p>
              <p className="mt-1 text-data leading-relaxed text-ink-muted">
                Every site, content version, media asset and licence activation in this organisation
                is scheduled for deletion. Live sites stop receiving content updates. This cannot be
                undone.
              </p>
            </div>
            <Button variant="danger" onClick={() => setDeleteOpen(true)}>
              Delete organisation
            </Button>
          </div>
        </section>
      </PermissionGate>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        destructive
        title="Delete this organisation?"
        confirmLabel="Delete permanently"
        pending={deleteMutation.isPending}
        description={
          <>
            <strong className="text-ink">{organisation.data?.name}</strong> and everything inside it
            will be removed. Sites served through the SDK will stop receiving updates.
          </>
        }
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/settings')({
  component: SettingsPage,
});
